/**
 * KAN-980: вкладка "Пул Telegram": аккаунты пула (выдача кабинету) или все
 * аккаунты платформы (возврат в пул), итог последнего переноса.
 */
import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { usePlatformTgPool, useReturnPlatformTgAccounts } from '@/hooks/usePlatform';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { filterByHealth, HEALTH_FILTERS, isHealthFilter, type HealthFilter } from '@/lib/telegramFlood';
import type { TgPoolAccount, TgPoolScope, TransferResponse } from '@/services/api/platform';
import { AssignDialog } from './AssignDialog';
import { ClearFloodDialog } from './ClearFloodDialog';
import { PoolSummary } from './PoolSummary';
import { TgPoolTable } from './TgPoolTable';
import { TransferResults } from './TransferResults';

const SCOPES: { value: TgPoolScope; label: string }[] = [
  { value: 'pool', label: 'Пул' },
  { value: 'all', label: 'Все аккаунты' },
];

export function TgPoolTab() {
  const [scope, setScope] = useState<TgPoolScope>('pool');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [assignOpen, setAssignOpen] = useState(false);
  const [result, setResult] = useState<TransferResponse | null>(null);
  const [health, setHealth] = useState<HealthFilter>('all');
  const [clearFloodFor, setClearFloodFor] = useState<TgPoolAccount | null>(null);
  const pool = usePlatformTgPool(scope);
  const visible = useMemo(() => filterByHealth(pool.data?.accounts ?? [], health), [pool.data, health]);
  const returnMutation = useReturnPlatformTgAccounts();

  const changeScope = (next: TgPoolScope) => {
    setScope(next);
    setSelected(new Set());
    returnMutation.reset();
  };

  const toggle = (accountId: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });

  const finish = (response: TransferResponse) => {
    setSelected(new Set());
    setResult(response);
  };

  const returnSelected = () =>
    returnMutation.mutate({ account_ids: [...selected] }, { onSuccess: finish });

  const returnError = returnMutation.isError
    ? extractErrorMessage(returnMutation.error, 'Не удалось вернуть аккаунты в пул')
    : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        {SCOPES.map((item) => (
          <Button
            key={item.value}
            size="sm"
            variant={scope === item.value ? 'default' : 'outline'}
            data-testid={`platform-pool-scope-${item.value}`}
            aria-pressed={scope === item.value}
            onClick={() => changeScope(item.value)}
          >
            {item.label}
          </Button>
        ))}
        <select
          aria-label="Фильтр по здоровью"
          data-testid="platform-pool-health-filter"
          className="h-9 rounded-md border border-gray-300 bg-white px-2 text-sm"
          value={health}
          onChange={(e) => {
            if (!isHealthFilter(e.target.value)) return;
            setHealth(e.target.value);
            setSelected(new Set());
          }}
        >
          {HEALTH_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <div className="ml-auto flex gap-2">
          {scope === 'pool' ? (
            <Button
              data-testid="platform-pool-assign-open"
              disabled={selected.size === 0}
              onClick={() => setAssignOpen(true)}
            >
              Выдать кабинету
            </Button>
          ) : (
            <Button
              data-testid="platform-pool-return"
              disabled={selected.size === 0 || returnMutation.isPending}
              onClick={returnSelected}
            >
              Вернуть в пул
            </Button>
          )}
        </div>
      </div>

      {pool.data && !pool.data.pool_configured && (
        <p data-testid="platform-pool-not-configured" className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
          Пул платформы не настроен: задайте PLATFORM_POOL_ORG_ID на сервере.
        </p>
      )}
      {pool.data?.summary && <PoolSummary summary={pool.data.summary} />}
      {pool.data?.pool_organization && (
        <p className="text-sm text-gray-500">Кабинет пула: {pool.data.pool_organization.name}</p>
      )}
      {returnError && (
        <p role="alert" className="text-sm text-red-600">
          {returnError}
        </p>
      )}
      {result && <TransferResults title="Итог переноса" result={result} onDismiss={() => setResult(null)} />}

      {pool.isLoading && <p className="text-sm text-gray-500">Загрузка...</p>}
      {pool.isError && (
        <p role="alert" className="text-sm text-red-600">
          Не удалось загрузить аккаунты
        </p>
      )}
      {pool.data && (
        <TgPoolTable
          accounts={visible}
          scope={scope}
          selected={selected}
          onToggle={toggle}
          onClearFlood={setClearFloodFor}
        />
      )}

      <AssignDialog
        open={assignOpen}
        accountIds={[...selected]}
        onClose={() => setAssignOpen(false)}
        onDone={finish}
      />
      <ClearFloodDialog account={clearFloodFor} onClose={() => setClearFloodFor(null)} />
    </div>
  );
}
